// A "Ducks"-style collection
// https://github.com/erikras/ducks-modular-redux

import { findKey, get, last, pick } from 'lodash';
import { setValidationResult } from 'validations';

// constants
import { searchStatuses, selectIdPrefix, sort } from 'constants/values';
import { applicationTypes } from 'constants/scripts';

// sub-ducks
import productInputsReducer, {
  actions as productInputsActions,
  setSeedSelectorProductInput 
} from 'edit/seed-product-selector/product-inputs';

// other ducks
import { closeSeedProductSelectorModal, setSeedProductInput } from 'current/uiwidgets';
import { validateSeedProductSelectorBrand, validateSeedProductSelectorRmValues } from 'current/validators';
import { setApplicationProducts } from 'products';
import {
  updateSeedApplicationProduct,
  updateSeedApplicationProductForZones
} from 'scripts';
import { getSeedApplicationsForZone } from 'scripts/zones/planting-selectors';

// selectors
import { getCropById, getCropIdByCrop } from 'crops/selectors';
import {
  getActiveCropDfpUuid,
  getActiveScript,
  getActiveSeedProductIds,
  getActiveUserId,
  getActiveFieldCountryCode
} from 'current/selectors';
import {
  getSeedProductRmValuesForCrop,
  getSearchParams,
  getSeedProductSelectorInputs
} from 'edit/seed-product-selector/selectors';
import { getSeedProductById } from 'products/planting-selectors';
import { getProductValidationValues } from 'user/selectors';

// loaders and parsers
import {
  parseSearchResults
} from 'edit/seed-product-selector/seedSelectorParser';

import { fetchSeedProductsSearchResultsFromSprout } from 'products/loaders';
import { parseSproutProducts } from 'products/productsParser';

// util
import { hasAction } from 'util/duck';
import { consolidateProductInputs } from './productInputsUtil';

// Action Types
export const typePrefix = 'vra/seed-product-selector/';
export const actions = [
  'RESET_SEARCH_STATE',
  'RESET_SEED_SELECTOR_PRODUCT_INPUTS',
  'SET_SEARCH_PARAMS',
  'SET_SEARCH_RESULTS',
  'SET_SEARCH_STATUS',

  // analytics
  'SEARCH_SEED_PRODUCTS',
  'NAVIGATE_WINFIELD_R7'
].reduce((result, key) => { result[key] = `${typePrefix}${key}`; return result; }, {});

const initialState = {
  productInputs: {
    // just set this outright -- the assumption is up to two hybrids
    seed: {
      [`${selectIdPrefix}1`]: {},
      [`${selectIdPrefix}2`]: {}
    }
  },
  searchStatus: searchStatuses.PRESEARCH,
  searchResults: [],
  searchResultsSort: { trait: 'brand', dir: sort.ASC},
  searchParams: {}
};


// Reducer

export default function reducer (
  state = {...initialState},
  action = {}
) {
  // Dispatch to sub-Duck reducers as appropriate.
  if (hasAction(productInputsActions, action.type)) {
    return {
      ...state,
      productInputs: productInputsReducer(state.productInputs, action)
    };
  }

  switch (action.type) {
    case actions.RESET_SEARCH_STATE:
      return Object.assign(state,
        pick(initialState, ['searchStatus', 'searchResults', 'searchResultsSort', 'searchParams']));

    case actions.RESET_SEED_SELECTOR_PRODUCT_INPUTS:
      return Object.assign(state, pick(initialState, ['productInputs']));

    case actions.SET_SEARCH_PARAMS:
      return {
        ...state,
        searchParams: Object.assign({}, state.searchParams, action.payload.searchParams)
      };

    case actions.SET_SEARCH_RESULTS:
      return {
        ...state,
        searchResults: action.payload.searchResults
      };

    case actions.SET_SEARCH_STATUS:
      return {
        ...state,
        searchStatus: action.payload.status
      };

    default:
      return state;
  }
}

// Action Creators


export const resetSeedSelectorProductInputs = () => {
  return {
    type: actions.RESET_SEED_SELECTOR_PRODUCT_INPUTS
  };
};

export const resetSearchState = () => {
  return {
    type: actions.RESET_SEARCH_STATE
  };
};

export const setSearchParams = (searchParams) => {
  return {
    type: actions.SET_SEARCH_PARAMS,
    payload: { searchParams }
  };
};


export const validateSeedSelectorSearchParams = ({ searchParams, resultedFromUserInput=true }) => {
  return (dispatch, getState) => {
    const state = getState();
    const brandsValidationMessages = validateSeedProductSelectorBrand(searchParams);
    dispatch(
      setValidationResult({
        key: 'seedProductSelectorBrands',
        resultedFromUserInput,
        rawValidatorResult: brandsValidationMessages
      })
    );

    const productValidationValues = getProductValidationValues(state);
    const cropName = getCropById(state, get(searchParams, 'cropId', '')) || '';
    const standardRm = get(productValidationValues, `PLANTING.${cropName.toUpperCase()}.RM`);
    const rawValidatorResult = validateSeedProductSelectorRmValues({ ...searchParams, standardRm });
    dispatch(
      setValidationResult({
        key: 'seedProductSelectorRmValues',
        resultedFromUserInput,
        rawValidatorResult,
        productValidationValues
      })
    );
  };
};

export const initializeSeedSelectorSearchParams = (crop) => {
  return (dispatch, getState) => {
    const state = getState();
    const rmValues = getSeedProductRmValuesForCrop(state, crop);
    const cropId = getCropIdByCrop(state, crop);
    const searchParams = {
      cropId,
      rmMax: last(rmValues),
      rmMin: rmValues[0]
    };
    dispatch(setSearchParams(searchParams));
    dispatch(validateSeedSelectorSearchParams({ searchParams, resultedFromUserInput: false }));
  };
};

export const updateSeedSelectorSearchParam = (newParams) => {
  return (dispatch, getState) => {
    const state = getState();
    const searchParams = {...getSearchParams(state), ...newParams};

    dispatch(setSearchParams(searchParams));
    dispatch(validateSeedSelectorSearchParams({ searchParams }));
  };
};

export const setSearchStatus = (status) => {
  return {
    type: actions.SET_SEARCH_STATUS,
    payload: { status }
  };
};

export const setSearchResults = ({ searchResults }) => {
  return {
    type: actions.SET_SEARCH_RESULTS,
    payload: {
      searchResults: searchResults
    }
  };
};

export const setSelectedResultsToActive = () => {
  return (dispatch, getState) => {
    let state = getState();
    const productInputs = consolidateProductInputs(getSeedProductSelectorInputs(state));

    // step through each seed selector input and set the
    // active seed inputs for the script
    Object.keys(productInputs).forEach((selectId) => {
      const input = productInputs[selectId];
      const currentProductId = get(input, 'currentProductId');

      const active = !!currentProductId;
      const products = currentProductId ? [getSeedProductById(state, currentProductId)] : [];
      // if currentProductId doesn't exist, this will blank out the input
      dispatch(setSeedProductInput({ active, currentProductId, products, selectId }));
    });

    // refetch state
    state = getState();
    const { id: scriptId, zones } = getActiveScript(state);
    const activeSeedProductIds = getActiveSeedProductIds(state);

    // if active product ids set above is 1, the the script should get
    // set to the 1 product across the board
    if (activeSeedProductIds.length === 1) {
      dispatch(updateSeedApplicationProductForZones({ scriptId, productId: activeSeedProductIds[0] }));

    } else {
      // otherwise, step through the zones and blank out any products
      // that don't exist anymore in the active products
      Object.values(zones).forEach((zone) => {
        const { zoneId } = zone.meta;
        const [{ product }] = getSeedApplicationsForZone(zone);
        const productId = get(product, 'id');
        if (productId && !activeSeedProductIds.includes(productId)) {
          dispatch(updateSeedApplicationProduct({ productId: null, scriptId, zoneId }));
        }
      });
    }

    dispatch(closeSeedProductSelectorModal());
  };
};

export const searchSeedProductsFromSelector = () => {
  return (dispatch, getState) => {
    const state = getState();
    const cropDfpUuid = getActiveCropDfpUuid(state);
    const userId = getActiveUserId(state);
    const fieldCountryCode = getActiveFieldCountryCode(state);
    const { brandUuid, rmMax, rmMin } = getSearchParams(state);

    dispatch(setSearchStatus(searchStatuses.PENDING));

    return fetchSeedProductsSearchResultsFromSprout({
      brandUuid,
      cropDfpUuid,
      userId,
      fieldCountryCode
    })
    .then(parseSproutProducts)
    .then((products) => {
      dispatch(setApplicationProducts({ applicationType: applicationTypes.SEED, products }));
      const searchResults = parseSearchResults({ searchResults: products, rmMax, rmMin });
      dispatch(setSearchResults({ searchResults }));
      dispatch(setSearchStatus(searchStatuses.COMPLETE));
    })
    .catch(() => {
      dispatch(setSearchStatus(searchStatuses.ERROR));
    });
  };
};

export const toggleSearchResult = (productId) => {
  return (dispatch, getState) => {
    const state = getState();
    const inputs = getSeedProductSelectorInputs(state);

    // find where this product id already exists or where there's not a product id already and update that
    const selectId = findKey(inputs, (input) => input.currentProductId === productId || !input.currentProductId);
    dispatch(setSeedSelectorProductInput(
      { currentProductId: get(inputs, `${selectId}.currentProductId`) ? undefined : productId, selectId }));
  };
};

export const unitTestPrivate = {
  initialState
};
